import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const { signIn, isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && isAdmin) {
      navigate("/admin/dashboard");
    }
  }, [isAdmin, loading, navigate]);

  // Check lockout status when email changes
  useEffect(() => {
    const checkLockout = async () => {
      if (!email) return;
      
      const { data: locked, error } = await supabase.rpc("is_account_locked", {
        user_email: email.toLowerCase(),
      });

      if (!error && locked) {
        setIsLocked(true);
        
        // Get remaining lockout time
        const { data: remaining } = await supabase.rpc("get_lockout_remaining", {
          user_email: email.toLowerCase(),
        });
        
        if (remaining) {
          setLockoutSeconds(remaining);
        }
      } else {
        setIsLocked(false);
        setLockoutSeconds(0);
      }
    };

    checkLockout();
  }, [email]);

  // Countdown timer for lockout
  useEffect(() => {
    if (lockoutSeconds > 0) {
      const timer = setInterval(() => {
        setLockoutSeconds((prev) => {
          if (prev <= 1) {
            setIsLocked(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [lockoutSeconds]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if account is locked
    if (isLocked) {
      toast.error("Conta bloqueada", {
        description: `Muitas tentativas incorretas. Tente novamente em ${Math.ceil(lockoutSeconds / 60)} minutos.`,
      });
      return;
    }

    setIsLoading(true);

    // Double-check lockout status before attempting login
    const { data: locked } = await supabase.rpc("is_account_locked", {
      user_email: email.toLowerCase(),
    });

    if (locked) {
      const { data: remaining } = await supabase.rpc("get_lockout_remaining", {
        user_email: email.toLowerCase(),
      });
      
      setIsLocked(true);
      setLockoutSeconds(remaining || 900);
      setIsLoading(false);
      
      toast.error("Conta bloqueada", {
        description: `Muitas tentativas incorretas. Tente novamente em ${Math.ceil((remaining || 900) / 60)} minutos.`,
      });
      return;
    }

    const { error } = await signIn(email, password);

    // Log the login attempt using secure RPC function
    await supabase.rpc("record_login_attempt", {
      attempt_email: email.toLowerCase(),
      attempt_success: !error,
      attempt_ip: null
    });

    if (error) {
      // Check if this was the 3rd failed attempt
      const { data: nowLocked } = await supabase.rpc("is_account_locked", {
        user_email: email.toLowerCase(),
      });

      if (nowLocked) {
        setIsLocked(true);
        setLockoutSeconds(900); // 15 minutes
        
        toast.error("Conta bloqueada", {
          description: "Muitas tentativas incorretas. Sua conta foi bloqueada por 15 minutos.",
        });
      } else {
        toast.error("Login falhou", {
          description: error.message || "Credenciais inválidas. Por favor, tente novamente.",
        });
      }
    } else {
      toast.success("Login realizado com sucesso!");
    }

    setIsLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Link>
          <CardTitle className="text-2xl font-bold">Admin Login</CardTitle>
          <CardDescription>
            Enter your credentials to access the admin dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLocked && (
            <Alert variant="destructive" className="mb-4">
              <ShieldAlert className="h-4 w-4" />
              <AlertTitle>Conta Bloqueada</AlertTitle>
              <AlertDescription>
                Muitas tentativas de login incorretas. Tente novamente em{" "}
                <strong>{Math.floor(lockoutSeconds / 60)}:{String(lockoutSeconds % 60).padStart(2, '0')}</strong> minutos.
              </AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading || isLocked}>
              {isLoading ? "Entrando..." : isLocked ? "Conta Bloqueada" : "Entrar"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
